import React, { useState, useEffect } from "react";
import { Layout } from "@/components/layout";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/providers/auth-provider";
import { useCurrentBusiness } from "@/hooks/use-current-business";
import { useEstimates, Estimate, CreateEstimateData } from "@/hooks/use-estimates";
import { useCustomers } from "@/hooks/use-customers";
import { useProducts } from "@/hooks/use-products";
import { useInvoices } from "@/hooks/use-invoices";
import { format } from "date-fns";
import { zodResolver } from "@hookform/resolvers/zod";

function safeFormatDate(date: any, fmt = "MMM d, yyyy"): string {
  if (!date) return "-";
  const d = new Date(date);
  if (isNaN(d.getTime())) return "-";
  return format(d, fmt);
}
import { useForm, useFieldArray } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { 
  FileSignature, 
  Plus, 
  RefreshCw, 
  Trash2, 
  MoreHorizontal, 
  Loader2, 
  Calendar, 
  Clock,
  Edit,
  Send,
  CheckCircle,
  X as XIcon,
  AlertCircle,
  Download,
  Mail,
  FileText,
  DollarSign,
  FileUp
} from "lucide-react";

// Create estimate form schema
const estimateFormSchema = z.object({
  customerId: z.string().min(1, "Customer is required"),
  issueDate: z.string().min(1, "Issue date is required"),
  expiryDate: z.string().min(1, "Expiry date is required"),
  status: z.enum(["draft", "sent", "accepted", "rejected", "expired", "converted"]).default("draft"),
  notes: z.string().optional(),
  terms: z.string().optional(),
  items: z.array(
    z.object({
      productId: z.string().optional(),
      description: z.string().min(1, "Description is required"),
      quantity: z.number().min(0.01, "Quantity must be greater than 0"),
      unitPrice: z.number().min(0, "Unit price must be positive"),
      taxRate: z.number().min(0, "Tax rate must be positive").max(100, "Tax rate cannot exceed 100%").default(0),
    })
  ).min(1, "At least one item is required"),
});

type EstimateFormValues = z.infer<typeof estimateFormSchema>;

interface EstimatesPageProps {
  customerId?: string;
}

export default function EstimatesPage({ customerId }: EstimatesPageProps = {}) {
  const { toast } = useToast();
  const { currentBusiness } = useCurrentBusiness();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [selectedEstimate, setSelectedEstimate] = useState<Estimate | null>(null);
  const [currentTab, setCurrentTab] = useState("all");
  
  // Fetch estimates
  const { 
    estimates, 
    isLoading, 
    error, 
    refetch,
    createEstimateMutation,
    updateEstimateMutation,
    deleteEstimateMutation,
    convertToInvoiceMutation
  } = useEstimates();
  
  // Fetch customers for dropdown with proper logging
  const { customers } = useCustomers();
  
  // Log customers for debugging
  useEffect(() => {
    console.log("Available customers for dropdown:", customers);
  }, [customers]);
  
  // Fetch products for dropdown
  const { products } = useProducts();
  
  // Auto-open create estimate dialog when coming from customer details
  useEffect(() => {
    // If customerId is provided, open the create dialog automatically
    if (customerId) {
      setIsCreateDialogOpen(true);
    }
  }, [customerId]);
  
  // Filter estimates by status for tabs
  const filteredEstimates = estimates?.filter(estimate => {
    if (currentTab === "all") return true;
    return estimate.status === currentTab;
  });
  
  // Form setup for creating estimates
  const form = useForm<EstimateFormValues>({
    resolver: zodResolver(estimateFormSchema),
    defaultValues: {
      customerId: "",
      issueDate: format(new Date(), "yyyy-MM-dd"),
      expiryDate: format(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), "yyyy-MM-dd"), // Default expiry date: 30 days from now
      status: "draft",
      notes: "",
      terms: "This estimate is valid for 30 days from the issue date.",
      items: [
        {
          productId: undefined,
          description: "",
          quantity: 1,
          unitPrice: 0,
          taxRate: 0,
        },
      ],
    },
  });
  
  // Field array for estimate items
  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "items",
  });
  
  // Calculate totals
  const calculateTotals = () => {
    const items = form.getValues("items");
    let subtotal = 0;
    let taxTotal = 0;
    
    items.forEach(item => {
      const lineTotal = item.quantity * item.unitPrice;
      subtotal += lineTotal;
      taxTotal += lineTotal * (item.taxRate / 100);
    });
    
    const total = subtotal + taxTotal;
    
    return { subtotal, taxTotal, total };
  };
  
  // Prefill product details when a product is selected
  const handleProductChange = (index: number, productId: string) => {
    if (!productId) return;
    
    const selectedProduct = products?.find(p => p.id.toString() === productId);
    if (selectedProduct) {
      form.setValue(`items.${index}.description`, selectedProduct.name);
      form.setValue(`items.${index}.unitPrice`, parseFloat(selectedProduct.price));
      form.setValue(`items.${index}.taxRate`, parseFloat(selectedProduct.taxRate || "0"));
    }
  };
  
  // Handle form submission
  const onSubmit = (data: EstimateFormValues) => {
    // Calculate totals for the estimate
    const totals = calculateTotals();
    
    // Generate a unique estimate number
    const estimateNumber = `EST-${Date.now().toString().slice(-6)}`;
    
    // Convert form data to match API expectations
    const estimateData: CreateEstimateData = {
      customerId: parseInt(data.customerId),
      issueDate: data.issueDate,
      expiryDate: data.expiryDate,
      status: data.status,
      notes: data.notes,
      terms: data.terms,
      estimateNumber: estimateNumber,
      subtotal: totals.subtotal.toString(),
      taxAmount: totals.taxTotal.toString(),
      total: totals.total.toString(),
      items: data.items.map(item => ({
        productId: item.productId ? parseInt(item.productId) : undefined,
        description: item.description,
        quantity: item.quantity,
        unitPrice: item.unitPrice,
        taxRate: item.taxRate,
        amount: (item.quantity * item.unitPrice),
        taxAmount: (item.quantity * item.unitPrice * (item.taxRate / 100)),
      })),
    };
    
    console.log("Submitting estimate data:", JSON.stringify(estimateData));
    
    createEstimateMutation.mutate(estimateData, {
      onSuccess: () => {
        toast({
          title: "Success",
          description: "Estimate created successfully",
        });
        setIsCreateDialogOpen(false);
        form.reset();
        
        // Force reload all estimates data
        setTimeout(() => {
          refetch();
          // If the above doesn't work, try a direct navigation
          window.location.href = window.location.href;
        }, 1000);
      }
    });
  };
  
  // Format currency display
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  // Get status badge variant
  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'draft': return 'secondary';
      case 'sent': return 'default';
      case 'accepted': return 'success';
      case 'rejected': return 'destructive';
      case 'expired': return 'outline';
      case 'converted': return 'default';
      default: return 'secondary';
    }
  };
  
  // Reset form when dialog closes
  useEffect(() => {
    if (!isCreateDialogOpen) {
      form.reset({
        customerId: "",
        issueDate: format(new Date(), "yyyy-MM-dd"),
        expiryDate: format(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), "yyyy-MM-dd"),
        status: "draft",
        notes: "",
        terms: "This estimate is valid for 30 days from the issue date.",
        items: [
          {
            productId: undefined,
            description: "",
            quantity: 1,
            unitPrice: 0,
            taxRate: 0,
          },
        ],
      });
    }
  }, [isCreateDialogOpen, form]);
  
  // Pre-select customer when customerId is provided from a customer details page
  useEffect(() => {
    if (customerId && isCreateDialogOpen) {
      // Set the customerId in the form
      form.setValue("customerId", customerId);
    }
  }, [customerId, isCreateDialogOpen, form]);

  if (!currentBusiness) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center min-h-[60vh]">
          <h2 className="text-2xl font-bold mb-4">No Business Selected</h2>
          <p className="text-muted-foreground mb-6">
            Please select a business to manage estimates
          </p>
        </div>
      </Layout>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <Layout>
        <div className="container mx-auto py-6">
          <div className="flex justify-center items-center h-[60vh]">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        </div>
      </Layout>
    );
  }

  // Error state
  if (error) {
    return (
      <Layout>
        <div className="container mx-auto py-6">
          <div className="flex flex-col items-center justify-center h-[60vh]">
            <AlertCircle className="h-12 w-12 text-destructive mb-4" />
            <h2 className="text-2xl font-bold mb-2">Failed to Load Estimates</h2>
            <p className="text-muted-foreground mb-4">
              {(error as Error).message || "An unexpected error occurred"}
            </p>
            <Button onClick={() => refetch()}>
              <RefreshCw className="h-4 w-4 mr-2" /> Try Again
            </Button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto py-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold">Estimates</h1>
            <p className="text-muted-foreground">
              Create and manage customer estimates and quotes
            </p>
          </div>
          <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="flex items-center gap-2">
                <Plus className="h-4 w-4" />
                New Estimate
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl">
              <DialogHeader>
                <DialogTitle>Create New Estimate</DialogTitle>
                <DialogDescription>
                  Create a new estimate for your customer.
                </DialogDescription>
              </DialogHeader>
              
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Customer Selection */}
                    <FormField
                      control={form.control}
                      name="customerId"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Customer</FormLabel>
                          <Select 
                            onValueChange={field.onChange} 
                            defaultValue={field.value}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select a customer" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {customers && customers.length > 0 ? (
                                customers.map(customer => (
                                  <SelectItem key={customer.id} value={customer.id.toString()}>
                                    {customer.name}
                                  </SelectItem>
                                ))
                              ) : (
                                <SelectItem value="__none__" disabled>
                                  No customers found
                                </SelectItem>
                              )}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    {/* Issue Date */}
                    <FormField
                      control={form.control}
                      name="issueDate"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Issue Date</FormLabel>
                          <FormControl>
                            <Input type="date" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    {/* Expiry Date */}
                    <FormField
                      control={form.control}
                      name="expiryDate"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Expiry Date</FormLabel>
                          <FormControl>
                            <Input type="date" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  
                  {/* Estimate Items */}
                  <div className="border rounded-md p-4">
                    <h3 className="font-medium mb-3">Estimate Items</h3>
                    <div className="space-y-4">
                      {fields.map((item, index) => (
                        <div key={item.id} className="grid grid-cols-12 gap-2 items-start">
                          {/* Product Selection */}
                          <div className="col-span-12 md:col-span-3">
                            <FormField
                              control={form.control}
                              name={`items.${index}.productId`}
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel className="text-xs">Product</FormLabel>
                                  <Select 
                                    onValueChange={(value) => {
                                      field.onChange(value);
                                      handleProductChange(index, value);
                                    }} 
                                    value={field.value}
                                  >
                                    <FormControl>
                                      <SelectTrigger>
                                        <SelectValue placeholder="Select or leave empty" />
                                      </SelectTrigger>
                                    </FormControl>
                                    <SelectContent>
                                      {products?.map(product => (
                                        <SelectItem key={product.id} value={product.id.toString()}>
                                          {product.name}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </FormItem>
                              )}
                            />
                          </div>
                          
                          {/* Description */}
                          <div className="col-span-12 md:col-span-3">
                            <FormField
                              control={form.control}
                              name={`items.${index}.description`}
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel className="text-xs">Description</FormLabel>
                                  <FormControl>
                                    <Input {...field} />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          </div>
                          
                          {/* Quantity */}
                          <div className="col-span-3 md:col-span-1">
                            <FormField
                              control={form.control}
                              name={`items.${index}.quantity`}
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel className="text-xs">Qty</FormLabel>
                                  <FormControl>
                                    <Input 
                                      type="number" 
                                      min="0" 
                                      step="0.01" 
                                      {...field} 
                                      onChange={e => field.onChange(parseFloat(e.target.value))}
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          </div>
                          
                          {/* Unit Price */}
                          <div className="col-span-3 md:col-span-2">
                            <FormField
                              control={form.control}
                              name={`items.${index}.unitPrice`}
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel className="text-xs">Price</FormLabel>
                                  <FormControl>
                                    <Input 
                                      type="number" 
                                      min="0" 
                                      step="0.01" 
                                      {...field} 
                                      onChange={e => field.onChange(parseFloat(e.target.value))}
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          </div>
                          
                          {/* Tax Rate */}
                          <div className="col-span-3 md:col-span-2">
                            <FormField
                              control={form.control}
                              name={`items.${index}.taxRate`}
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel className="text-xs">Tax Rate %</FormLabel>
                                  <FormControl>
                                    <Input 
                                      type="number" 
                                      min="0" 
                                      max="100" 
                                      step="0.01" 
                                      {...field} 
                                      onChange={e => field.onChange(parseFloat(e.target.value))}
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          </div>
                          
                          {/* Remove Button */}
                          <div className="col-span-3 md:col-span-1 pt-7">
                            <Button 
                              type="button" 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => remove(index)}
                              disabled={fields.length === 1}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        </div>
                      ))}
                      
                      {/* Add Item Button */}
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => append({
                          productId: undefined,
                          description: "",
                          quantity: 1,
                          unitPrice: 0,
                          taxRate: 0,
                        })}
                      >
                        <Plus className="h-4 w-4 mr-2" /> Add Item
                      </Button>
                    </div>
                  </div>
                  
                  {/* Totals Section */}
                  <div className="flex justify-end">
                    <div className="w-full max-w-xs space-y-2">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Subtotal:</span>
                        <span>{formatCurrency(calculateTotals().subtotal)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Tax:</span>
                        <span>{formatCurrency(calculateTotals().taxTotal)}</span>
                      </div>
                      <div className="flex justify-between font-bold">
                        <span>Total:</span>
                        <span>{formatCurrency(calculateTotals().total)}</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Notes and Terms */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="notes"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Notes</FormLabel>
                          <FormControl>
                            <Textarea 
                              placeholder="Additional notes for the customer"
                              className="h-20"
                              {...field} 
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="terms"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Terms & Conditions</FormLabel>
                          <FormControl>
                            <Textarea 
                              placeholder="Estimate terms and conditions"
                              className="h-20"
                              {...field} 
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" disabled={createEstimateMutation.isPending}>
                      {createEstimateMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                      Create Estimate
                    </Button>
                  </DialogFooter>
                </form>
              </Form>
            </DialogContent>
          </Dialog>
        </div>

        <Tabs value={currentTab} onValueChange={setCurrentTab}>
          <TabsList className="mb-4">
            <TabsTrigger value="all">All</TabsTrigger>
            <TabsTrigger value="draft">Draft</TabsTrigger>
            <TabsTrigger value="sent">Sent</TabsTrigger>
            <TabsTrigger value="accepted">Accepted</TabsTrigger>
            <TabsTrigger value="rejected">Rejected</TabsTrigger>
            <TabsTrigger value="expired">Expired</TabsTrigger>
          </TabsList>

          <TabsContent value={currentTab} className="space-y-4">
            {/* Estimates Table */}
            {filteredEstimates && filteredEstimates.length > 0 ? (
              <Card>
                <CardContent className="p-6">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Estimate #</TableHead>
                        <TableHead>Customer</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Expiry Date</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredEstimates.map((estimate) => (
                        <TableRow key={estimate.id}>
                          <TableCell className="font-medium">{estimate.estimateNumber}</TableCell>
                          <TableCell>{estimate.customerName}</TableCell>
                          <TableCell>{safeFormatDate(estimate.issueDate || estimate.date)}</TableCell>
                          <TableCell>{safeFormatDate(estimate.expiryDate || estimate.expiry_date)}</TableCell>
                          <TableCell>
                            <Badge variant={getStatusBadgeVariant(estimate.status)}>
                              {estimate.status.charAt(0).toUpperCase() + estimate.status.slice(1)}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">{formatCurrency(estimate.total)}</TableCell>
                          <TableCell>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" className="h-8 w-8 p-0">
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuLabel>Actions</DropdownMenuLabel>
                                <DropdownMenuItem 
                                  onClick={() => {
                                    setSelectedEstimate(estimate);
                                    setIsViewDialogOpen(true);
                                  }}
                                >
                                  <FileSignature className="h-4 w-4 mr-2" />
                                  View Details
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => {
                                    // Handle edit action
                                  }}
                                >
                                  <Edit className="h-4 w-4 mr-2" />
                                  Edit
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                {estimate.status === 'draft' && (
                                  <DropdownMenuItem
                                    onClick={() => {
                                      // Handle mark as sent
                                      updateEstimateMutation.mutate({
                                        id: estimate.id,
                                        data: { status: 'sent' }
                                      });
                                    }}
                                  >
                                    <Send className="h-4 w-4 mr-2" />
                                    Mark as Sent
                                  </DropdownMenuItem>
                                )}
                                {estimate.status === 'sent' && (
                                  <>
                                    <DropdownMenuItem
                                      onClick={() => {
                                        // Handle mark as accepted
                                        updateEstimateMutation.mutate({
                                          id: estimate.id,
                                          data: { status: 'accepted' }
                                        });
                                      }}
                                    >
                                      <CheckCircle className="h-4 w-4 mr-2" />
                                      Mark as Accepted
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      onClick={() => {
                                        // Handle mark as rejected
                                        updateEstimateMutation.mutate({
                                          id: estimate.id,
                                          data: { status: 'rejected' }
                                        });
                                      }}
                                    >
                                      <XIcon className="h-4 w-4 mr-2" />
                                      Mark as Rejected
                                    </DropdownMenuItem>
                                  </>
                                )}
                                {estimate.status === 'accepted' && (
                                  <DropdownMenuItem
                                    onClick={() => {
                                      // Handle convert to invoice
                                      convertToInvoiceMutation.mutate(estimate.id);
                                    }}
                                  >
                                    <FileText className="h-4 w-4 mr-2" />
                                    Convert to Invoice
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuItem
                                  onClick={() => {
                                    // Handle download PDF
                                    toast({
                                      title: "Feature in progress",
                                      description: "PDF download will be available soon",
                                    });
                                  }}
                                >
                                  <Download className="h-4 w-4 mr-2" />
                                  Download PDF
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <AlertDialog>
                                  <AlertDialogTrigger asChild>
                                    <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                                      <Trash2 className="h-4 w-4 mr-2 text-destructive" />
                                      <span className="text-destructive">Delete</span>
                                    </DropdownMenuItem>
                                  </AlertDialogTrigger>
                                  <AlertDialogContent>
                                    <AlertDialogHeader>
                                      <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                                      <AlertDialogDescription>
                                        This action cannot be undone. This will permanently delete the
                                        estimate and remove it from our servers.
                                      </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                                      <AlertDialogAction
                                        onClick={() => {
                                          deleteEstimateMutation.mutate(estimate.id);
                                        }}
                                      >
                                        Delete
                                      </AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="p-6">
                  <div className="text-center py-10">
                    <FileSignature className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-medium mb-2">No Estimates Yet</h3>
                    <p className="text-muted-foreground mb-4">
                      {currentTab === "all"
                        ? "You haven't created any estimates yet. Create your first estimate to get started."
                        : `You don't have any ${currentTab} estimates.`}
                    </p>
                    <Button onClick={() => setIsCreateDialogOpen(true)}>
                      <Plus className="h-4 w-4 mr-2" />
                      Create Estimate
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
        
        {/* View Estimate Dialog */}
        {selectedEstimate && (
          <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
            <DialogContent className="max-w-4xl">
              <DialogHeader>
                <DialogTitle>Estimate {selectedEstimate.estimateNumber}</DialogTitle>
                <DialogDescription>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge variant={getStatusBadgeVariant(selectedEstimate.status)}>
                      {selectedEstimate.status.charAt(0).toUpperCase() + selectedEstimate.status.slice(1)}
                    </Badge>
                    <span className="text-sm text-muted-foreground">
                      Created on {safeFormatDate(selectedEstimate.issueDate || selectedEstimate.date, 'MMMM d, yyyy')}
                    </span>
                  </div>
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <h3 className="text-sm font-medium mb-1">For:</h3>
                    <p className="text-base font-semibold">{selectedEstimate.customerName}</p>
                  </div>
                  <div className="space-y-1">
                    <div className="flex items-center text-sm">
                      <Calendar className="h-4 w-4 mr-2 text-muted-foreground" />
                      <span className="text-muted-foreground mr-2">Issue Date:</span>
                      <span>{safeFormatDate(selectedEstimate.issueDate || selectedEstimate.date, 'MMMM d, yyyy')}</span>
                    </div>
                    <div className="flex items-center text-sm">
                      <Clock className="h-4 w-4 mr-2 text-muted-foreground" />
                      <span className="text-muted-foreground mr-2">Expiry Date:</span>
                      <span>{safeFormatDate(selectedEstimate.expiryDate || selectedEstimate.expiry_date, 'MMMM d, yyyy')}</span>
                    </div>
                  </div>
                </div>
                
                <div className="border rounded-md">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Description</TableHead>
                        <TableHead className="text-right">Qty</TableHead>
                        <TableHead className="text-right">Unit Price</TableHead>
                        <TableHead className="text-right">Tax Rate</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedEstimate.items?.map((item) => (
                        <TableRow key={item.id}>
                          <TableCell>{item.description}</TableCell>
                          <TableCell className="text-right">{item.quantity}</TableCell>
                          <TableCell className="text-right">{formatCurrency(item.unitPrice)}</TableCell>
                          <TableCell className="text-right">{item.taxRate}%</TableCell>
                          <TableCell className="text-right">{formatCurrency(item.amount)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                    <TableFooter>
                      <TableRow>
                        <TableCell colSpan={4} className="text-right">Subtotal</TableCell>
                        <TableCell className="text-right">{formatCurrency(selectedEstimate.subtotal)}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={4} className="text-right">Tax</TableCell>
                        <TableCell className="text-right">{formatCurrency(selectedEstimate.taxAmount)}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={4} className="text-right font-bold">Total</TableCell>
                        <TableCell className="text-right font-bold">{formatCurrency(selectedEstimate.total)}</TableCell>
                      </TableRow>
                    </TableFooter>
                  </Table>
                </div>
                
                {(selectedEstimate.notes || selectedEstimate.terms) && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {selectedEstimate.notes && (
                      <div>
                        <h3 className="text-sm font-medium mb-1">Notes:</h3>
                        <p className="text-sm text-muted-foreground">{selectedEstimate.notes}</p>
                      </div>
                    )}
                    {selectedEstimate.terms && (
                      <div>
                        <h3 className="text-sm font-medium mb-1">Terms & Conditions:</h3>
                        <p className="text-sm text-muted-foreground">{selectedEstimate.terms}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              <DialogFooter className="gap-2">
                <div className="flex flex-wrap gap-2 justify-end">
                  {selectedEstimate.status === 'draft' && (
                    <Button
                      variant="outline"
                      onClick={() => {
                        updateEstimateMutation.mutate({
                          id: selectedEstimate.id,
                          data: { status: 'sent' }
                        });
                        setIsViewDialogOpen(false);
                      }}
                    >
                      <Send className="h-4 w-4 mr-2" />
                      Mark as Sent
                    </Button>
                  )}
                  {selectedEstimate.status === 'sent' && (
                    <>
                      <Button
                        variant="outline"
                        onClick={() => {
                          updateEstimateMutation.mutate({
                            id: selectedEstimate.id,
                            data: { status: 'accepted' }
                          });
                          setIsViewDialogOpen(false);
                        }}
                      >
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Mark as Accepted
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => {
                          updateEstimateMutation.mutate({
                            id: selectedEstimate.id,
                            data: { status: 'rejected' }
                          });
                          setIsViewDialogOpen(false);
                        }}
                      >
                        <XIcon className="h-4 w-4 mr-2" />
                        Mark as Rejected
                      </Button>
                    </>
                  )}
                  {selectedEstimate.status === 'accepted' && (
                    <Button
                      variant="outline"
                      onClick={() => {
                        convertToInvoiceMutation.mutate(selectedEstimate.id);
                        setIsViewDialogOpen(false);
                      }}
                    >
                      <FileText className="h-4 w-4 mr-2" />
                      Convert to Invoice
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    onClick={() => {
                      toast({
                        title: "Feature in progress",
                        description: "Email sending will be available soon",
                      });
                    }}
                  >
                    <Mail className="h-4 w-4 mr-2" />
                    Send Email
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      toast({
                        title: "Feature in progress",
                        description: "PDF download will be available soon",
                      });
                    }}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download PDF
                  </Button>
                </div>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>
    </Layout>
  );
}